package top.wcpe.allincore.ui.web.asset

internal object AssetPathPolicy {
    fun external(input: String?): String {
        val segments = decodeSegments(requirePathReference(input))
        if (segments.any { it == ".." }) fail("外部资产路径不得包含父目录段")
        return canonical(segments)
    }

    fun canonicalDirectory(input: String): String = canonicalBaseSegments(input).joinToString("/")

    fun relative(baseCanonicalDirectory: String, input: String?): String {
        val stack = canonicalBaseSegments(baseCanonicalDirectory).toMutableList()
        decodeSegments(requirePathReference(input)).forEach { segment ->
            if (segment == "..") {
                if (stack.isEmpty()) fail("内部资产路径越出授权根")
                stack.removeAt(stack.lastIndex)
            } else {
                stack += segment
            }
        }
        return canonical(stack)
    }

    private fun requirePathReference(input: String?): String {
        val value = input ?: fail("资产路径不能为空")
        if (value.isEmpty()) fail("资产路径不能为空")
        if (value.any(::isAsciiControl)) fail("资产路径不得包含控制字符")
        if ('\\' in value) fail("资产路径不得包含反斜杠")
        if (value.startsWith('/')) fail("资产路径必须是相对路径")
        if (SCHEME.matches(value.substringBefore('/'))) fail("资产路径不得包含 scheme 或盘符")
        if ('?' in value || '#' in value) fail("资产路径不得包含查询参数或片段")
        return value
    }

    private fun decodeSegments(value: String): List<String> = value.split('/').map { raw ->
        if (raw.isEmpty()) fail("资产路径不得包含空段")
        val decoded = decodeSegment(raw)
        if (decoded == ".") fail("资产路径不得包含当前目录段")
        decoded
    }

    internal fun pathnameSegments(pathname: String): List<String>? = try {
        if (!pathname.startsWith('/')) return null
        val rawSegments = pathname.removePrefix("/").split('/').toMutableList()
        if (rawSegments.lastOrNull().isNullOrEmpty()) rawSegments.removeLastOrNull()
        if (rawSegments.isEmpty()) emptyList() else rawSegments.map(::decodePathnameSegment)
    } catch (ignored: Throwable) {
        null
    }

    private fun decodePathnameSegment(raw: String): String {
        if (raw.isEmpty()) fail("资产 pathname 不得包含空段")
        val decoded = decodeSegment(raw)
        if (decoded == "." || decoded == "..") fail("资产 pathname 不得包含点目录段")
        return decoded
    }

    private fun decodeSegment(raw: String): String {
        if (ENCODED_SEPARATOR.containsMatchIn(raw)) fail("资产路径不得编码分隔符")
        val decoded = try {
            decodeURIComponent(raw)
        } catch (ignored: Throwable) {
            fail("资产路径包含畸形百分号或 UTF-8 编码")
        }
        if (decoded.any(::isAsciiControl)) fail("资产路径解码后不得包含控制字符")
        if (SCHEME.matches(decoded)) fail("资产路径解码后不得包含 scheme 或盘符")
        if ('/' in decoded || '\\' in decoded) fail("资产路径解码后不得包含分隔符")
        if ('?' in decoded || '#' in decoded) fail("资产路径解码后不得包含查询参数或片段")
        if (SECOND_ENCODING.containsMatchIn(decoded)) fail("资产路径不得包含二次百分号编码")
        return decoded.toNfc()
    }

    private fun canonical(segments: List<String>): String {
        if (segments.isEmpty() || segments.any { it.isEmpty() }) fail("规范资产路径不能为空")
        return segments.joinToString("/")
    }

    private fun canonicalBaseSegments(base: String): List<String> {
        if (base.isEmpty()) return emptyList()
        if (base.startsWith('/') || base.endsWith('/')) fail("内部基准目录不是规范相对路径")
        return base.split('/').map { segment ->
            if (segment.isEmpty() || segment == "." || segment == "..") fail("内部基准目录包含非法分段")
            if (segment.any(::isAsciiControl) || '\\' in segment || '?' in segment || '#' in segment) {
                fail("内部基准目录包含非法字符")
            }
            if (segment != segment.toNfc()) fail("内部基准目录必须使用 NFC 规范形式")
            segment
        }
    }
}

internal object AssetContainmentPolicy {
    fun contains(
        baseOrigin: String,
        basePathname: String,
        candidateOrigin: String,
        candidatePathname: String,
    ): Boolean {
        if (baseOrigin != candidateOrigin) return false
        val base = pathnameSegments(basePathname) ?: return false
        val candidate = pathnameSegments(candidatePathname) ?: return false
        if (candidate.size < base.size) return false
        return base.indices.all { index -> base[index] == candidate[index] }
    }

    private fun pathnameSegments(pathname: String): List<String>? = AssetPathPolicy.pathnameSegments(pathname)
}

private fun isAsciiControl(char: Char): Boolean = char.code <= 0x1F || char.code in 0x7F..0x9F

private fun String.toNfc(): String = asDynamic().normalize("NFC") as String

private fun fail(message: String): Nothing = throw AssetPolicyException(message)

private val SCHEME = Regex("^[A-Za-z][A-Za-z0-9+.-]*:.*$")
private val ENCODED_SEPARATOR = Regex("%(?:2[fF]|5[cC])")
private val SECOND_ENCODING = Regex("%[0-9A-Fa-f]{2}")

private external fun decodeURIComponent(encoded: String): String
